import React, { useState, useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { string } from 'yup';
import InfoIcon from '@mui/icons-material/Info';
import { rtColors } from '@ritualco/ritual-frontend-components';

import {
  FormattedMessage,
  useFormatMessage,
} from '@artemis/integrations/contentful/utils';
import {
  ButtonContainer,
  Container,
  Body,
  PrimaryButton,
  Title,
} from './shared';
import { logConnectWorkEmailImpression } from './analytics';
import { VERIFICATION_ERROR_CODE } from '../constants';

const emailSchema = string().email();

const InputContainer = styled.div`
  padding-bottom: 12px;
`;

const Input = styled.input`
  padding: 10px 20px;
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.palette.grey['100']};
  border-style: none;
  border-radius: 4pt;
  box-sizing: border-box;
  display: block;
  margin-bottom: 8px;
`;

const Note = styled.p`
  margin-top: 16px;
  ${props => props.theme.typography.caption};
  color: ${props =>
    props.error
      ? props.theme.palette.dark.error
      : props.theme.rtColors.black1000};
`;

const Error = styled.div`
  ${props => props.theme.typography.caption};
  color: ${props => props.theme.palette.dark.error};
`;

const Form = styled.form`
  margin-top: 8px;
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  margin-left: 24px;
`;

const ConnectEmail = ({ onNext, isLoading, errorCode }) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [highlightExample, setHighlightExample] = useState(false);

  const inputLabel = useFormatMessage({
    entry: 'teams.connectEmail.emailLabel',
  });
  const inputPlaceholder = useFormatMessage({
    entry: 'teams.connectEmail.inputPlaceholder',
  });
  const emailValidationError = useFormatMessage({
    entry: 'teams.connectEmail.emailValidationError',
  });
  const emailDomainError = useFormatMessage({
    entry: 'teams.connectEmail.domainNotAllowedError',
  });
  const genericError = useFormatMessage({
    entry: 'errors.tryAgain',
  });
  const benefitsList = useFormatMessage({
    entry: 'teams.connectEmail.teamBenefits',
  }).split('\n');

  const onSubmit = async e => {
    e.preventDefault();
    setErrorMessage('');
    setHighlightExample(false);

    try {
      await emailSchema.validate(email);
      onNext(email);
    } catch (err) {
      setErrorMessage(emailValidationError);
    }
  };

  useEffectOnce(() => {
    logConnectWorkEmailImpression();
  });

  // Set error message based on error code
  useEffect(() => {
    if (errorCode === VERIFICATION_ERROR_CODE.EMAIL_DOMAIN_NOT_ALLOWED) {
      setHighlightExample(true);
      setErrorMessage(emailDomainError);
    } else if (errorCode === VERIFICATION_ERROR_CODE.TEAM_INVITE_REQUIRED) {
      // do not show anything
    } else if (errorCode) {
      setErrorMessage(genericError);
    }
  }, [errorCode]);

  return (
    <Container>
      <Title>
        <FormattedMessage entry="teams.connectEmail.title" />
      </Title>
      <Body>
        <FormattedMessage entry="teams.connectEmail.teamsIntro" />
        <List>
          {benefitsList.map(benefit => (
            <li key={benefit}>{benefit}</li>
          ))}
        </List>
      </Body>
      <Body>
        <FormattedMessage entry="teams.connectEmail.getStarted" />
      </Body>
      <Form>
        <InputContainer>
          <Input
            type="email"
            aria-label={inputLabel}
            placeholder={inputPlaceholder}
            value={email}
            name="work-email"
            onChange={e => setEmail(e.target.value)}
            autoFocus
          />
          {/* TODO(IE-2420): refactor into a shared error message component */}
          {errorMessage && (
            <Error>
              <InfoIcon
                sx={{
                  color: rtColors.red700,
                  fontSize: 14,
                  marginRight: '6px',
                  verticalAlign: 'middle',
                }}
              />

              {errorMessage}
            </Error>
          )}
          <Note error={highlightExample}>
            <FormattedMessage entry="teams.connectEmail.example" />
          </Note>
        </InputContainer>

        <ButtonContainer>
          <PrimaryButton
            isDisabled={!email}
            isLoading={isLoading}
            onClick={onSubmit}
          >
            <FormattedMessage entry="teams.connectEmail.verifyButton" />
          </PrimaryButton>
        </ButtonContainer>
      </Form>
    </Container>
  );
};

ConnectEmail.propTypes = {
  onNext: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
};

export default ConnectEmail;
